import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to manage Telegram Mini App BackButton
 *
 * Usage:
 * - useTelegramBackButton() - navigates back in history
 * - useTelegramBackButton(onBack) - calls custom handler
 * - useTelegramBackButton(onBack, { confirm: true, confirmMessage: '...' }) - with confirmation
 */
interface BackButtonOptions {
  confirm?: boolean;
  confirmMessage?: string;
}

export function useTelegramBackButton(
  onBack?: () => void,
  options?: BackButtonOptions
) {
  const navigate = useNavigate();
  const handlerRef = useRef<(() => void) | null>(null);

  // Update ref on every render so the stable wrapper always calls the latest logic
  handlerRef.current = useCallback(() => {
    if (options?.confirm) {
      const message = options.confirmMessage || 'Вы уверены, что хотите выйти?';
      const confirmed = window.confirm(message);
      if (confirmed) {
        if (onBack) {
          onBack();
        } else {
          navigate(-1);
        }
      }
    } else {
      if (onBack) {
        onBack();
      } else {
        navigate(-1);
      }
    }
  }, [navigate, onBack, options?.confirm, options?.confirmMessage]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg?.BackButton) return;

    // Register a stable wrapper once — it delegates to handlerRef.current
    // so we never need to remove/re-add the handler
    tg.BackButton.show();
    tg.BackButton.onClick(() => handlerRef.current?.());

    return () => {
      tg.BackButton.hide();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// Confirmation preset for practices
export function useTelegramBackButtonWithConfirm(
  isRunning: boolean,
  onBack: () => void,
  confirmMessage?: string
) {
  useTelegramBackButton(onBack, {
    confirm: isRunning,
    confirmMessage: confirmMessage || 'Завершить практику?',
  });
}
